import { useEffect, useState, useMemo } from 'react';
import type { OperationToQuickloadUrl } from '../operation-to-quickload-url.generated';
import { cacheFactory } from '../cacheFactory';
import { getValidPreloadsForRoute } from './getValidPreloadsForRoute';
import type { DocumentNode } from 'graphql';
import type { TrelloWindow } from '@trello/window-types';

declare const window: TrelloWindow;

type Operations = (keyof typeof OperationToQuickloadUrl)[];

export const useQuickLoad = ({
  waitOn,
  document,
  skip,
}: {
  waitOn: Operations;
  document: DocumentNode;
  skip?: boolean;
}) => {
  // getValidPreloadsForRoute and getPreloadsForRoute are rather expensive,
  // and since this renders throughout the app it will cause performance problems.
  // It's worth attempting to be as efficient as possible in this code.
  const validPreloadsForInitialPath = useMemo(
    () => getValidPreloadsForRoute(),
    [],
  );
  const initialPathname = useMemo(() => window.location.pathname, []);
  const isValidOperationToWaitOn = waitOn.every((operation) =>
    validPreloadsForInitialPath.includes(operation),
  );
  const isOnInitialPath = initialPathname === window.location.pathname;
  const canUseWaitOn =
    // dev didn't supply waitOn argument
    waitOn.length > 0 &&
    // dev specified None, which means skip all this
    waitOn[0] !== 'None' &&
    // dev attempted to wait on preload that isn't valid for the initial route
    isValidOperationToWaitOn &&
    // we don't support route transitions since requests aren't made through QuickLoad anymore
    isOnInitialPath;
  // If we are in Storybook just move on as there are no quickloads
  const isStorybook = window?.__TRELLO_STORYBOOK_PREVIEW ?? false;
  const canQuickLoad = canUseWaitOn && !skip && !isStorybook;
  const [isQuickLoading, setIsQuickLoading] = useState(
    canQuickLoad ? true : false,
  );

  useEffect(() => {
    if (!canQuickLoad) {
      if (isQuickLoading) {
        setIsQuickLoading(false);
      }
      return;
    }

    const start = async () => {
      for (const operation of waitOn) {
        await cacheFactory.waitForQueryHydratedTo(operation, 'Apollo');
      }
      setIsQuickLoading(false);
    };

    start();
    // intentionally disabled since we don't support changing
    // the operations that you are waiting on
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [skip]);

  return isQuickLoading;
};
